import React, { useImperativeHandle, useRef, useState } from 'react';
import useShowPDF from "@util/hook/useShowPDF";
import DeleteOrderDialog from '@icoach/accountBook/dialog/DeleteOrderDialog';
import ImportAchDialog from '@icoach/accountBook/dialog/ImportAchDialog';
import OrderAllowanceDialog from '@icoach/accountBook/dialog/OrderAllowanceDialog';
import OrderInvoiceVoidDialog from '@icoach/accountBook/dialog/OrderInvoiceVoidDialog';
import PaymentOrderDialog from '@icoach/accountBook/dialog/PaymentOrderDialog';
import ReinitOrderDialog from '@icoach/accountBook/dialog/ReinitOrderDialog';
import ResetOrderInvoiceDialog from '@icoach/accountBook/dialog/ResetOrderInvoiceDialog';
import UploadTriplicateAllowanceCertificateDialog from "@icoach/accountBook/dialog/UploadTriplicateAllowanceCertificateDialog";
import { isEqual, isFunction } from '@util/utils';
import TransactionListDialog from "@icoach/accountBook/dialog/TransactionListDialog";

const CREATE_ORDER_DIALOG = 'createOrder';
const DELETE_ORDER_DIALOG = 'deleteOrder';
const EDIT_MEMO_DIALOG = 'editMemo';
const IMPORT_ACH_DIALOG = 'importAch';
const ORDERS_DIALOG = 'orders';
const PETTY_MONEY_DIALOG = 'pettyMoney';
const PAYMENT_ORDER_DIALOG = 'paymentOrder';
const REINIT_ORDER_DIALOG = 'reinitOrder';
const REMIT_ALLOWANCE_ABANDON_DIALOG = 'remitAllowanceAbandon';
const REMIT_ALLOWANCE_DIALOG = 'remitAllowance';
const REMIT_INVOICE_VOID_DIALOG = 'remitInvoiceVoid';
const RESET_ORDER_INVOICE_DIALOG = 'resetOrderInvoice';
const SETTLE_CASH_DIALOG = 'settleCash';
const SETTLE_CREDIT_DIALOG = 'settleCredit';
const SHOPPING_MENU_DIALOG = 'shoppingMenu';
const ORDER_ALLOWANCE_VOID_DIALOG = 'orderAllowanceVoid';
const ORDER_ALLOWANCE_DIALOG = 'orderAllowance';
const ORDER_INVOICE_ABANDON_DIALOG = 'orderInvoiceVoid';
const TRANSACTION_LIST_DIALOG = 'transactionListDialog';
const UPLOAD_TRIPLICATE_ALLOWANCE_CERTIFICATE_DIALOG = "uploadTriplicateAllowanceCertificateDialog";

const AccountBookDialogBox = React.forwardRef((props, ref) => {
	const showPDF = useShowPDF();
	const [openDialog, setOpenDialog] = useState('');
	const [sourceData, setSourceData] = useState(null);
	const refreshRef = useRef(null);

	const handleDialogClose = () => {
		setOpenDialog(null);
		setSourceData(null);
	};

	const doRefresh = (...arg) => {
		if (isFunction(refreshRef.current)) {
			setTimeout(() => refreshRef.current(...arg), 5);
		}
	};

	useImperativeHandle(
		ref,
		() => ({
			openCreateOrderDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(CREATE_ORDER_DIALOG);
			},
			openDeleteOrderDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(DELETE_ORDER_DIALOG);
			},
			openEditMemoDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(EDIT_MEMO_DIALOG);
			},
			openImportAchDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(IMPORT_ACH_DIALOG);
			},
			openOrderInvoiceVoidDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(ORDER_INVOICE_ABANDON_DIALOG);
			},
			openOrderAllowanceDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(ORDER_ALLOWANCE_DIALOG);
			},
			openOrderAllowanceVoidDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(ORDER_ALLOWANCE_VOID_DIALOG);
			},
			openOrdersDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(ORDERS_DIALOG);
			},
			openPaymentOrderDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(PAYMENT_ORDER_DIALOG);
			},
			openPettyMoneyDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(PETTY_MONEY_DIALOG);
			},
			openReinitOrderDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(REINIT_ORDER_DIALOG);
			},
			openRemitAllowanceAbandonDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(REMIT_ALLOWANCE_ABANDON_DIALOG);
			},
			openRemitAllowanceDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(REMIT_ALLOWANCE_DIALOG);
			},
			openRemitInvoiceVoidDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(REMIT_INVOICE_VOID_DIALOG);
			},
			openResetOrderInvoiceDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(RESET_ORDER_INVOICE_DIALOG);
			},
			openSettleCashDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(SETTLE_CASH_DIALOG);
			},
			openSettleCreditDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(SETTLE_CREDIT_DIALOG);
			},
			openShoppingMenuDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(SHOPPING_MENU_DIALOG);
			},
			openTransactionListDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(TRANSACTION_LIST_DIALOG);
			},
			openUploadTriplicateAllowanceCertificateDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(UPLOAD_TRIPLICATE_ALLOWANCE_CERTIFICATE_DIALOG);
			},
			openTriplicateAllowanceCertificatePdfDialog: (targetData = {}) => {
				const { certificatePath, orderID, orderReturnID } = targetData;
				const pdfUrl =
					certificatePath ||
					(orderID && orderReturnID
						? `/pdf/orders/${orderID}/returns/${orderReturnID}/triplicate-uniform-certificate.pdf`
						: "");
				if (!pdfUrl) return;
				showPDF({ open: true, pdfUrl });
			},
			openTriplicateAllowanceConfirmPdfDialog: (targetData = {}) => {
				const { confirmPath } = targetData;
				if (!confirmPath) return;
				showPDF({ open: true, pdfUrl: confirmPath });
			},
		}),
		// eslint-disable-next-line
		[]
	);

	if (!openDialog) return null;

	return (
		<React.Fragment>
			<DeleteOrderDialog open={isEqual(openDialog, DELETE_ORDER_DIALOG)} onClose={handleDialogClose} refresh={doRefresh} sourceData={sourceData} />
			<ImportAchDialog open={isEqual(openDialog, IMPORT_ACH_DIALOG)} onClose={handleDialogClose} refresh={doRefresh} sourceData={sourceData} />
			<OrderAllowanceDialog open={isEqual(openDialog, ORDER_ALLOWANCE_DIALOG)} onClose={handleDialogClose} refresh={doRefresh} sourceData={sourceData} />
			<OrderInvoiceVoidDialog open={isEqual(openDialog, ORDER_INVOICE_ABANDON_DIALOG)} onClose={handleDialogClose} refresh={doRefresh} sourceData={sourceData} />
			<PaymentOrderDialog open={isEqual(openDialog, PAYMENT_ORDER_DIALOG)} onClose={handleDialogClose} refresh={doRefresh} sourceData={sourceData} />
			<ReinitOrderDialog open={isEqual(openDialog, REINIT_ORDER_DIALOG)} onClose={handleDialogClose} refresh={doRefresh} sourceData={sourceData} />
			<ResetOrderInvoiceDialog open={isEqual(openDialog, RESET_ORDER_INVOICE_DIALOG)} onClose={handleDialogClose} refresh={doRefresh} sourceData={sourceData} />
			<TransactionListDialog open={isEqual(openDialog, TRANSACTION_LIST_DIALOG)} onClose={handleDialogClose} sourceData={sourceData} />
			<UploadTriplicateAllowanceCertificateDialog
				open={isEqual(openDialog, UPLOAD_TRIPLICATE_ALLOWANCE_CERTIFICATE_DIALOG)}
				onClose={handleDialogClose}
				refresh={doRefresh}
				sourceData={sourceData}
			/>
		</React.Fragment>
	);
});

export default AccountBookDialogBox;
